/**
 * Phase 15 Plan 15-02 — TanStack Query v5 hooks for the Monthly Summary + Month-Lock feature.
 *
 * Mirrors the `useLogsheetApproverQueries` pattern from Plan 14-02:
 *  - Typed query/mutation hooks over the `logsheet-monthly.ts` service layer.
 *  - `staleTime: 30_000` on list queries.
 *  - Upload mutation invalidates all affected caches on success.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/lib/api/error';
import {
  downloadMonthlySummaryCsv,
  downloadMonthlySummaryPdf,
  getEligibleForMonthlyDocumentRequest,
  getEntriesForMachineMonthRequest,
  getMonthlySummaryRequest,
  uploadMonthlyDocumentsRequest,
} from '@/services/logsheet-monthly';
import type {
  EligibleMonthlyTuple,
  MonthlyEntryItem,
  MonthlySummaryListQuery,
  MonthlySummaryRow,
  UploadMonthlyDocumentsResult,
} from '@/types/logsheet-monthly';
import type { PaginatedResult } from '@/types/roles';

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

/** Paginated monthly summary table (per-machine aggregation for one month). */
export function useMonthlySummaryQuery(query: MonthlySummaryListQuery) {
  return useQuery<PaginatedResult<MonthlySummaryRow>, ApiError>({
    queryKey: ['monthly', 'summary', query],
    queryFn: () => getMonthlySummaryRequest(query),
    staleTime: 30_000,
    enabled: query.month !== '',
  });
}

/** Drill-through entries for a (machineId, month) tuple. */
export function useEntriesForMachineMonthQuery(machineId: number, month: string) {
  return useQuery<readonly MonthlyEntryItem[], ApiError>({
    queryKey: ['monthly', 'entries', machineId, month],
    queryFn: () => getEntriesForMachineMonthRequest(machineId, month),
    staleTime: 30_000,
    enabled: Number.isFinite(machineId) && machineId > 0 && month !== '',
  });
}

/** Eligible (machine, month) tuples for monthly-document upload. */
export function useEligibleForMonthlyDocumentQuery(siteId?: number) {
  return useQuery<readonly EligibleMonthlyTuple[], ApiError>({
    queryKey: ['monthly', 'eligible', siteId ?? null],
    queryFn: () => getEligibleForMonthlyDocumentRequest(siteId),
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Mutation hook
// ---------------------------------------------------------------------------

interface UploadMonthlyDocumentsVars {
  readonly machineId: number;
  readonly month: string;
  readonly combinedDailyLogsheet: File;
  readonly monthlySummaryLogsheet: File;
  /** REVIEW FF-012: dialog passes an AbortController.signal so cancel/unmount
   *  aborts the in-flight multipart upload mid-stream. */
  readonly signal?: AbortSignal;
}

/**
 * Upload Combined Daily + Monthly Summary PDFs for a (machine, month) tuple.
 * On success invalidates:
 *   - ['monthly', 'summary']   — summary table shows locked=true
 *   - ['monthly', 'eligible']  — tuple removed from eligible list
 *   - ['checker', 'pending']   — checker list shows monthLocked=true (when backend adds it)
 *   - ['approver', 'pending']  — approver list shows monthLocked=true (when backend adds it)
 *   - ['logsheets-maker']      — maker list shows monthLocked=true (when backend adds it)
 */
export function useUploadMonthlyDocumentsMutation() {
  const qc = useQueryClient();
  return useMutation<UploadMonthlyDocumentsResult, ApiError, UploadMonthlyDocumentsVars>({
    mutationFn: ({ signal, ...rest }) =>
      uploadMonthlyDocumentsRequest(rest, signal ? { signal } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['monthly', 'summary'] });
      void qc.invalidateQueries({ queryKey: ['monthly', 'eligible'] });
      // Round-trip invalidations for Phase 12/13/14 list pages — these will reflect
      // monthLocked=true once the backend list projections are extended.
      void qc.invalidateQueries({ queryKey: ['checker', 'pending'] });
      void qc.invalidateQueries({ queryKey: ['approver', 'pending'] });
      void qc.invalidateQueries({ queryKey: ['logsheets-maker'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Re-exported non-hook download helpers (mirrors useLogsheetApproverQueries pattern)
// ---------------------------------------------------------------------------

export { downloadMonthlySummaryCsv, downloadMonthlySummaryPdf };
